// ReasonSelectDialog — общий презентационный диалог выбора причины отказа (V17 W4).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { rejectionReasonsApi } from '@/api/rejectionReasons';

/** Ключ react-query каталога активных причин отказа. */
const REJECTION_REASONS_QUERY_KEY = ['rejection-reasons'] as const;

/** Пропсы общего диалога выбора причины из каталога rejection-reasons. */
export interface ReasonSelectDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Закрытие без подтверждения. */
  onClose: () => void;
  /** Подтверждение: выбранный id причины. */
  onConfirm: (rejectionReasonId: string) => void;
  /** Мутация в полёте — блокирует повторный submit. */
  pending?: boolean;
  /** id заголовка для aria-labelledby. */
  titleId: string;
  /** Заголовок диалога (уже переведённая строка). */
  title: string;
  /** Подпись селекта причины. */
  label: string;
  /** Подпись кнопки подтверждения. */
  confirmLabel: string;
  /** Текст ошибки загрузки каталога. */
  loadError: string;
  /** Подпись кнопки отмены. */
  cancelLabel: string;
}

/**
 * Презентационный диалог выбора причины отказа: каталог, confirm/cancel, loading/error.
 * Тексты — пропсы (i18n остаётся у обёрток); без выбора confirm неактивен.
 */
export function ReasonSelectDialog({
  open,
  onClose,
  onConfirm,
  pending = false,
  titleId,
  title,
  label,
  confirmLabel,
  loadError,
  cancelLabel,
}: ReasonSelectDialogProps): JSX.Element {
  const [reasonId, setReasonId] = useState('');
  const catalogQuery = useQuery({
    queryKey: REJECTION_REASONS_QUERY_KEY,
    queryFn: () => rejectionReasonsApi.list(),
    enabled: open,
    retry: false,
  });
  const reasons = catalogQuery.data ?? [];
  const selectDisabled = catalogQuery.isLoading || pending;
  const confirmDisabled = !reasonId || pending;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {catalogQuery.isError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <TextField
            select
            fullWidth
            required
            margin="dense"
            label={label}
            value={reasonId}
            onChange={(e) => setReasonId(e.target.value)}
            disabled={selectDisabled}
            inputProps={{ 'aria-label': label }}
          >
            {reasons.map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {reason.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button variant="contained" disabled={confirmDisabled} onClick={() => onConfirm(reasonId)}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
